import React, { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { resetPassword, clearErrors } from '../../actions/userAction';
import TextField from '@mui/material/TextField';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import FormSidebar from './FormSidebar';

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { token } = useParams();

  const { error, success, loading } = useSelector((state) => state.forgotPassword);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (newPassword.length < 8) {
      enqueueSnackbar('Password length must be at least 8 characters', { variant: 'warning' });
      return;
    }
    if (newPassword !== confirmPassword) {
      enqueueSnackbar("Passwords don't match", { variant: 'error' });
      return;
    }

    // Dispatch reset password action
    const formData = new FormData();
    formData.set('password', newPassword);
    formData.set('confirmPassword', confirmPassword);
    dispatch(resetPassword(token, formData));
  };

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
      dispatch(clearErrors());
    }
    if (success) {
      enqueueSnackbar('Password updated successfully', { variant: 'success' });
      navigate('/login');
    }
  }, [dispatch, error, success, navigate, enqueueSnackbar]);

  return (
    <>
      <MetaData
        title="Password Reset | Siddhi Creatives"
        description="Reset your password securely at Siddhi Creatives. Follow the steps to regain access to your account and protect your privacy."
        keywords="password reset, reset password, account recovery, regain access, secure account, Siddhi Creatives, password recovery"
        ogTitle="Password Reset | Siddhi Creatives"
        ogDescription="Easily reset your password at Siddhi Creatives. Protect your account by securely changing your password."
      />

      {loading && <BackdropLoader />}

      <main className="w-full mt-16 sm:pt-20 sm:mt-0">
        <div className="flex sm:w-4/6 sm:mt-4 m-auto mb-7 bg-white shadow-lg">
          <FormSidebar
            title="Reset Password"
            tag="Get access to your Orders, Wishlist, and Recommendations"
          />

          <div className="flex-1 overflow-hidden">
            <h2 className="text-center text-2xl font-medium mt-6 text-gray-800">Reset Password</h2>

            <div className="text-center py-10 px-4 sm:px-14">
              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col w-full gap-4">
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />

                  <div className="flex flex-col gap-2.5 mt-2 mb-32">
                    <button
                      type="submit"
                      className="text-white py-3 w-full bg-primary-orange shadow hover:shadow-lg rounded-sm font-medium"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </form>

              <Link to="/register" className="font-medium text-sm text-primary-blue">
                New to Siddhi Creatives? Create an account
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default memo(ResetPassword);
